import { format } from "date-fns";
import { normalizeFirestoreDate } from "../../../lib/firestoreDate";
import { getkCal } from "../../../lib/nutritionUtils";

/**
 * Map raw reportHealth documents to chart rows (same numeric shape as coach-console nutrition series).
 * @param {{ reportData?: object, createdOn?: * }[]} docs
 * @returns {object[]}
 */
export function mapReportHealthDocsToChartRows(docs) {
  if (!Array.isArray(docs) || docs.length === 0) return [];

  const rows = docs.map((doc) => {
    const rd = doc.reportData || {};
    const micro =
      rd.micronutrients && typeof rd.micronutrients === "object" ? rd.micronutrients : {};
    const fiberVal = typeof micro.fiber === "number" ? micro.fiber : 0;

    let kCal = 0;
    if (typeof rd.kCal === "number" && rd.kCal > 0) {
      kCal = rd.kCal;
    } else if (rd.carbs > 0 || rd.fats > 0 || rd.proteins > 0) {
      kCal = getkCal({
        carbs: rd.carbs,
        fats: rd.fats,
        proteins: rd.proteins,
      });
    }

    const createdOn = normalizeFirestoreDate(doc.createdOn);
    const co = createdOn ?? new Date(0);

    return {
      ...rd,
      micronutrients: micro,
      kCal: parseFloat(Number(kCal).toFixed(2)),
      carbs: parseFloat(Number(rd.carbs || 0).toFixed(2)),
      fats: parseFloat(Number(rd.fats || 0).toFixed(2)),
      proteins: parseFloat(Number(rd.proteins || 0).toFixed(2)),
      weight: parseFloat(Number(rd.weight || 0).toFixed(2)),
      water: parseFloat(Number(rd.water || 0).toFixed(2)),
      sleepHours: parseFloat(Number(rd.sleepHours || 0).toFixed(1)),
      fiber: parseFloat(Number(fiberVal).toFixed(2)),
      steps: parseFloat(Number(rd.steps || 0).toFixed(0)),
      averageHeartRate: parseFloat(Number(rd.averageHeartRate || 0).toFixed(0)),
      createdOn: co,
      createdOnString: format(co, "MMM d"),
    };
  });

  return rows.sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime());
}

/**
 * Averages over days with value &gt; 0 (matches coach NutritionCard pattern).
 * @param {object[]} chartRows
 */
export function computeHealthAverages(chartRows) {
  if (!chartRows?.length) {
    return {
      proteins: 0,
      carbs: 0,
      fats: 0,
      calories: 0,
      water: 0,
      fiber: 0,
      sleepHours: 0,
      steps: 0,
      weight: 0,
      counts: {},
    };
  }

  const sum = {
    proteins: 0,
    carbs: 0,
    fats: 0,
    calories: 0,
    water: 0,
    fiber: 0,
    sleepHours: 0,
    steps: 0,
    weight: 0,
  };
  const counts = {
    proteins: 0,
    carbs: 0,
    fats: 0,
    calories: 0,
    water: 0,
    fiber: 0,
    sleepHours: 0,
    steps: 0,
    weight: 0,
  };

  for (const d of chartRows) {
    const calories =
      d.kCal > 0
        ? d.kCal
        : d.proteins > 0 || d.carbs > 0 || d.fats > 0
          ? getkCal({ proteins: d.proteins, carbs: d.carbs, fats: d.fats })
          : 0;

    if (d.proteins > 0) {
      sum.proteins += d.proteins;
      counts.proteins++;
    }
    if (d.carbs > 0) {
      sum.carbs += d.carbs;
      counts.carbs++;
    }
    if (d.fats > 0) {
      sum.fats += d.fats;
      counts.fats++;
    }
    if (calories > 0) {
      sum.calories += calories;
      counts.calories++;
    }
    if (d.water > 0) {
      sum.water += d.water;
      counts.water++;
    }
    if (d.fiber > 0) {
      sum.fiber += d.fiber;
      counts.fiber++;
    }
    if (d.sleepHours > 0) {
      sum.sleepHours += d.sleepHours;
      counts.sleepHours++;
    }
    if (d.steps > 0) {
      sum.steps += d.steps;
      counts.steps++;
    }
    if (d.weight > 0) {
      sum.weight += d.weight;
      counts.weight++;
    }
  }

  return {
    proteins: counts.proteins > 0 ? Math.round(sum.proteins / counts.proteins) : 0,
    carbs: counts.carbs > 0 ? Math.round(sum.carbs / counts.carbs) : 0,
    fats: counts.fats > 0 ? Math.round(sum.fats / counts.fats) : 0,
    calories: counts.calories > 0 ? Math.round(sum.calories / counts.calories) : 0,
    water: counts.water > 0 ? Math.round(sum.water / counts.water) : 0,
    fiber: counts.fiber > 0 ? Math.round(sum.fiber / counts.fiber) : 0,
    sleepHours:
      counts.sleepHours > 0 ? Math.round((sum.sleepHours / counts.sleepHours) * 10) / 10 : 0,
    steps: counts.steps > 0 ? Math.round(sum.steps / counts.steps) : 0,
    weight: counts.weight > 0 ? Math.round((sum.weight / counts.weight) * 10) / 10 : 0,
    counts,
  };
}

const MAX_X_AXIS_TICKS = 6;

export function sparseCategoryTicks(lineData, maxTicks = MAX_X_AXIS_TICKS) {
  if (!lineData?.length) return [];
  const n = lineData.length;
  if (n <= maxTicks) {
    return lineData.map((d) => d.createdOnString);
  }
  const idxSet = new Set();
  for (let t = 0; t < maxTicks; t++) {
    idxSet.add(Math.round((t / (maxTicks - 1)) * (n - 1)));
  }
  const ordered = [...idxSet].sort((a, b) => a - b);
  const seen = new Set();
  const out = [];
  for (const i of ordered) {
    const s = lineData[i].createdOnString;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

export function filterRowsForMetric(chartType, data) {
  if (!data?.length) return [];
  if (chartType === "kCal") return data.filter((d) => d.kCal > 0);
  if (chartType === "proteins") return data.filter((d) => d.proteins > 0);
  if (chartType === "carbs") return data.filter((d) => d.carbs > 0);
  if (chartType === "fats") return data.filter((d) => d.fats > 0);
  if (chartType === "weight") return data.filter((d) => d.weight > 0);
  if (chartType === "water") return data.filter((d) => d.water > 0);
  if (chartType === "fiber") return data.filter((d) => d.fiber > 0);
  if (chartType === "sleepHours") return data.filter((d) => d.sleepHours > 0);
  if (chartType === "steps") return data.filter((d) => d.steps > 0);
  if (chartType === "averageHeartRate") return data.filter((d) => (d.averageHeartRate || 0) > 0);
  return data;
}

export function defaultMetricForSeries(data) {
  if (!data?.length) return "kCal";
  if (data.some((d) => d.water > 0)) return "water";
  if (data.some((d) => d.fiber > 0)) return "fiber";
  return "kCal";
}
